import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { messaggioErrore } from '../../utils/agenda';
import { linkWhatsApp } from '../../utils/percorso';
import Icona from '../cliente/Icone';

// Consigli del motore a regole (backend/consigli.py): sotto un'attività (riga dorata con il perché e un'azione)
// oppure "Suggerito · non in agenda" con Aggiungi / ✕. Scartato non torna, eseguito sparisce.

// La ✕ ("Non mi interessa") toglie il suggerito per sempre: prima la chiamata partiva senza guardare com'era
// andata — se falliva la riga restava lì senza una parola e il bottone si poteva ripremere, se riusciva il
// suggerito spariva in silenzio. Ora il bottone si spegne mentre si aspetta e la risposta si legge nel toast.
// (Non c'è un "Annulla": il server non ha una rotta per rimettere indietro uno scarto — vedi Richieste.)
export const useScarta = (consiglio, onFatto) => {
  const [attesa, setAttesa] = useState(false);
  const scarta = async () => {
    setAttesa(true);
    try {
      await axios.post(`${API}/consigli/${consiglio.id}/scarta`);
      toast.success('Tolto dai suggeriti');
      if (onFatto) onFatto();
    } catch (e) { toast.error(messaggioErrore(e, 'Non tolto dai suggeriti. Riprovate.')); } finally { setAttesa(false); }
  };
  return { scarta, attesa };
};

export const eseguiAzione = async (consiglio, navigate) => {
  const a = consiglio.azione || {};
  if (a.tipo === 'sposta' && consiglio.voce_id) {
    await axios.put(`${API}/agenda/${consiglio.voce_id}`, { ora: a.ora });
    toast.success(`Spostata alle ${a.ora}`);
  } else if (a.tipo === 'sollecita' && a.richiesta_id) {
    await axios.post(`${API}/richieste/${a.richiesta_id}/sollecita`);
    toast.success('Sollecito inviato al cliente');
  } else if (a.tipo === 'chiedi' && a.cliente_id) {
    await axios.post(`${API}/clienti/${a.cliente_id}/richieste`, { tipi: a.tipi || [] });
    toast.success('Richiesta inviata al cliente');
  } else if (a.tipo === 'whatsapp') {
    const link = linkWhatsApp(a.telefono);
    if (!link) { toast.error('Manca il numero di telefono'); return false; }
    window.open(`${link}${a.testo ? `?text=${encodeURIComponent(a.testo)}` : ''}`, '_blank', 'noopener');
  } else if (a.link) {
    navigate(a.link);
    return false; // aprire non è eseguire
  } else {
    return false;
  }
  await axios.post(`${API}/consigli/${consiglio.id}/fatto`).catch(() => {});
  return true;
};

export const ConsiglioRiga = ({ consiglio, onFatto }) => {
  const navigate = useNavigate();
  const [attesa, setAttesa] = useState(false);
  if (!consiglio) return null;
  const azione = async () => {
    setAttesa(true);
    try { if (await eseguiAzione(consiglio, navigate) && onFatto) onFatto(); } catch (e) { toast.error(messaggioErrore(e, 'Non riuscito. Riprovate.')); } finally { setAttesa(false); }
  };
  return (
    <div className="tip" data-testid="consiglio">
      <Icona nome="stella" />
      <div>
        {consiglio.testo}: <i>{consiglio.perche}</i>
        {consiglio.azione?.etichetta && <button type="button" onClick={azione} disabled={attesa}>{consiglio.azione.etichetta}</button>}
      </div>
    </div>
  );
};

// "via Cavour 8 potrebbe andare anche a Vasile B. e Igor B.": un Proponi per ciascun cliente (mappatura incrociata).
// Proporre a uno non chiude il suggerito: sparisce da solo quando tutti hanno la casa (o con ✕).
const ProponiA = ({ s, onFatto }) => {
  const [attesa, setAttesa] = useState(null);
  const [fatti, setFatti] = useState({});
  const { scarta, attesa: scartando } = useScarta(s, onFatto);
  const proponi = async (c) => {
    setAttesa(c.id);
    try {
      await axios.post(`${API}/clienti/${c.id}/archivio/${s.azione.immobile_id}/proponi`);
      setFatti((f) => ({ ...f, [c.id]: true }));
      toast.success(`Proposta a ${c.nome}: gli arriva «Volete vederla?»`);
    } catch (e) { toast.error(messaggioErrore(e, 'Proposta non mandata. Riprovate.')); } finally { setAttesa(null); }
  };
  return (
    <div className="sug per-altri" data-testid="suggerito">
      <div style={{ minWidth: 0 }}>
        <span className="kk">Suggerito · va bene anche ad altri clienti</span>
        <b>{s.testo}</b><span>{s.perche}</span>
        <div className="proponi-a">
          {(s.azione.clienti || []).map((c) => (
            <button type="button" key={c.id} className={`btn sm${fatti[c.id] ? '' : ' p'}`} disabled={attesa === c.id || fatti[c.id]} onClick={() => proponi(c)}
              title={[c.perche, c.manca ? `manca: ${c.manca}` : null].filter(Boolean).join(' · ')}>
              {fatti[c.id] ? `Proposta a ${c.nome} ✓` : `Proponi a ${c.nome}`} <small>{c.punteggio}</small>
            </button>
          ))}
          <Link className="btn sm" to={`/immobili/${s.azione.immobile_id}`}>Apri la casa</Link>
        </div>
      </div>
      <button type="button" className="x" aria-label="Non mi interessa" disabled={scartando} onClick={scarta}>✕</button>
    </div>
  );
};

export const Suggerito = ({ s, onFatto }) => {
  if (s.azione?.tipo === 'proponi_a') return <ProponiA s={s} onFatto={onFatto} />;
  return <SuggeritoSemplice s={s} onFatto={onFatto} />;
};

const SuggeritoSemplice = ({ s, onFatto }) => {
  const navigate = useNavigate();
  const [attesa, setAttesa] = useState(false);
  const { scarta, attesa: scartando } = useScarta(s, onFatto);
  const aggiungi = async () => {
    setAttesa(true);
    try {
      await axios.post(`${API}/agenda/suggeriti/${s.id}/aggiungi`, {});
      toast.success('In agenda');
      if (onFatto) onFatto();
    } catch (e) { toast.error(messaggioErrore(e, 'Non aggiunto in agenda. Riprovate.')); } finally { setAttesa(false); }
  };
  const agisci = async () => {
    setAttesa(true);
    try { if (await eseguiAzione(s, navigate) && onFatto) onFatto(); } catch (e) { toast.error(messaggioErrore(e, 'Non riuscito. Riprovate.')); } finally { setAttesa(false); }
  };
  const principale = s.azione?.tipo === 'aggiungi' ? null : s.azione;
  return (
    <div className="sug" data-testid="suggerito">
      <div style={{ minWidth: 0 }}>
        <span className="kk">Suggerito · non in agenda</span>
        <b>{s.testo}</b><span>{s.perche}</span>
      </div>
      <div style={{ display: 'flex', gap: '.3rem', alignItems: 'center' }}>
        {principale && <button type="button" className={`btn sm${principale.tipo === 'whatsapp' ? ' w' : ' p'}`} disabled={attesa || scartando} onClick={agisci}>{principale.etichetta}</button>}
        <button type="button" className={`btn sm${principale ? '' : ' p'}`} disabled={attesa || scartando} onClick={aggiungi}>Aggiungi</button>
        <button type="button" className="x" aria-label="Non mi interessa" disabled={scartando} onClick={scarta}>✕</button>
      </div>
    </div>
  );
};
